import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AccountingFilterRequest, AccountingDocSearchItem } from "@/models/accounting";
import { styled } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { formatAmount } from "@/util";
import InfiniteScroll from "react-infinite-scroll-component";
import { AccountingDocType } from "@/models/enums";
import filtersStore from "@/stores/filtersStore";

interface AccountingDocsTableProps {
    setFilter: Function;
    filter: AccountingFilterRequest;
    accountingDocs: AccountingDocSearchItem[];
    total: number;
    fetchMore: any;
}

const DetailsBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
          }

          &.${buttonUnstyledClasses.root} {
            width: auto;
            height: 26px;
            font-size: 14px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
          }`
);

function AccountingsDocsTable(props: AccountingDocsTableProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    function getArrowClass(sort: string) {
        if (sort === props.filter.orderBy) {
            if (props.filter.orderAsc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    const setNext = () => {
        filtersStore.setAccountingFilterData(null);
        props.fetchMore();
    };

    const sortBy = (sortBy: string) => {
        filtersStore.setAccountingFilterData(null);
        if (props.filter.orderBy !== sortBy) {
            props.setFilter({
                ...props.filter,
                orderAsc: true,
                orderBy: sortBy,
                pageIndex: 0,
            });
        } else {
            props.setFilter({
                ...props.filter,
                orderAsc: !props.filter.orderAsc,
                orderBy: sortBy,
                pageIndex: 0,
            });
        }
    };

    useEffect(() => {
        let offsetHeight = 138;
        const accountingContainer = document.getElementById("accounting-list-grid");
        if (!accountingContainer) {
            return;
        }
        const element = document.getElementById("accounting-filter-block");
        if (element) {
            offsetHeight += Number(element.clientHeight);
        }
        accountingContainer.style.height = `calc(100vh - ${offsetHeight}px)`;
    });

    return (
        <div id="accounting-list-grid" className="employee-list-grid">
            <InfiniteScroll
                dataLength={props.accountingDocs?.length}
                next={setNext}
                hasMore={props.accountingDocs?.length < props.total}
                loader={false}
                scrollableTarget="accounting-list-grid"
                style={{ overflow: "inherit" }}
            >
                <div className="fake-thead"></div>
                <table>
                    <thead>
                        <tr>
                            <td width={"150px"} className="cursor-pointer" onClick={() => sortBy("Id")}>
                                <div className="table-number-format-name">
                                    {t("accounting.documentId")}
                                    <i className={`icon-f ${getArrowClass("Id")} fs-12`} />
                                </div>
                            </td>
                            <td width={"200px"} onClick={() => sortBy("DocType")}>
                                {t("accounting.docType")}
                                <i className={`icon-f ${getArrowClass("DocType")} fs-12`} />
                            </td>
                            <td width={"250px"} onClick={() => sortBy("DocNumber")}>
                                <div className="table-number-format-name">
                                    {t("accounting.documentNumber")}
                                    <i className={`icon-f ${getArrowClass("DocNumber")} fs-12`} />
                                </div>
                            </td>
                            <td width={"200px"}>{t("orders.externalSystem")}</td>
                            <td width={"200px"}>
                                <div className="table-number-format-name">{t("accounting.docAmount")}</div>
                            </td>
                            <td style={{ width: "140px", textAlign: "center" }}>{t("accounting.info")}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {props.accountingDocs?.map((doc) => (
                            <tr key={doc.baseAccountingDocumentId}>
                                <td>
                                    <div className="table-number-format-value">{doc.baseAccountingDocumentId}</div>
                                </td>
                                <td>{t(`enums.AccountingDocType.${doc.docType}`)}</td>
                                <td>
                                    <div className="link-element table-number-format-value">
                                        {doc.docType.toString() ===
                                        AccountingDocType[AccountingDocType.Receipt].toString() ? (
                                            <span
                                                onClick={() =>
                                                    navigate(`/accounting/receipt/${doc.baseAccountingDocumentId}`)
                                                }
                                            >
                                                {doc.documentNumber}
                                            </span>
                                        ) : doc.docType.toString() ===
                                          AccountingDocType[AccountingDocType.Invoice].toString() ? (
                                            <span
                                                onClick={() =>
                                                    navigate(`/accounting/invoice/${doc.baseAccountingDocumentId}`)
                                                }
                                            >
                                                {doc.documentNumber}
                                            </span>
                                        ) : (
                                            ""
                                        )}
                                    </div>
                                </td>
                                <td>{doc.externalSystem}</td>
                                <td>
                                    <div className="table-number-format-value">
                                        {doc.docType.toString() ===
                                        AccountingDocType[AccountingDocType.Receipt].toString() ? (
                                            <span
                                                style={{
                                                    color: "#2EA984",
                                                }}
                                            >
                                                {formatAmount(doc.total, doc.currencyId)}
                                            </span>
                                        ) : (
                                            <span
                                                style={{
                                                    color: "#C03658",
                                                }}
                                            >
                                                {formatAmount(doc.total, doc.currencyId)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        {doc.docType.toString() ===
                                        AccountingDocType[AccountingDocType.Receipt].toString() ? (
                                            <DetailsBtn
                                                className="basic-button"
                                                onClick={() =>
                                                    navigate(`/accounting/receipt/${doc.baseAccountingDocumentId}`)
                                                }
                                            >
                                                {t("orders.button.info")}
                                            </DetailsBtn>
                                        ) : (
                                            <span
                                                onClick={() =>
                                                    navigate(`/accounting/invoice/${doc.baseAccountingDocumentId}`)
                                                }
                                                className="link-element"
                                            >
                                                {t("accounting.connectedOrders")}
                                            </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </InfiniteScroll>
        </div>
    );
}

export default AccountingsDocsTable;
